import {
  IconButton,
  Box,
  useTheme,
  Grid2,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import AppButton from "../buttons/AppButton";
import CustomTypography from "../form/CustomTypography";
import { Address } from "../../../../models/Address/Address";
import AddressCard from "../cards/AddressCard";
import { useEffect, useState } from "react";
import { observer } from "mobx-react";

interface SelectAddressPopupProps {
  label: string;
  list?: Address[];
  deleteAddress?: (id: number) => void;
  toggleAddressPopup: () => void;
  onDone: (address: Address | null) => void;
  open: boolean;
  selectedAddress?: Address | null;
  canCreateNewAddress?: boolean;
  canDeleteAddress?: boolean;
  addNewAddressbtnOnClick: () => void;
  id: string;
  disableAddButton?: boolean;
}

const SelectAddressPopup: React.FC<SelectAddressPopupProps> = ({
  label,
  list,
  toggleAddressPopup,
  open,
  selectedAddress,
  deleteAddress,
  canCreateNewAddress = true,
  canDeleteAddress = true,
  onDone,
  addNewAddressbtnOnClick,
  id,
  disableAddButton = false,
}) => {
  const theme = useTheme();
  const [selectedAddressInPopup, setSelectedAddressInPopup] =
    useState<Address | null>(selectedAddress || null);

  const handleClose = () => {
    toggleAddressPopup();
  };

  const handleDeleteAddress = (id: number) => {
    if (deleteAddress) deleteAddress(id);
  };

  const handleDone = () => {
    onDone(selectedAddressInPopup!);
    toggleAddressPopup();
  };

  useEffect(() => {
    setSelectedAddressInPopup(selectedAddress || null);
  }, [selectedAddress]);

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ pl: 4, pr: 4 }}>
        <CustomTypography
          fontWeight={700}
          variant="heading4"
          style={{ marginTop: "0.5rem" }}
        >
          {label}
        </CustomTypography>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <CustomTypography
            color="#7C7A7F"
            variant="heading5"
            sx={{ marginTop: "2rem" }}
          >
            You can edit the selected address from the add order screen
          </CustomTypography>
          {canCreateNewAddress && (
            <AppButton
              variant="tertiary"
              label="Add New"
              startIcon={<AddIcon />}
              onClick={() => {
                addNewAddressbtnOnClick();
              }}
              customStyles={{
                backgroundColor: "transparent",
                padding: 1,
                margin: 0,
                fontSize: "14px",
                color: theme.palette.customColors.pinkDark,
                "&:hover": {
                  backgroundColor: "transparent",
                  color: theme.palette.customColors.pinkDark,
                  boxShadow: "none",
                },
              }}
              disabled={disableAddButton}
            />
          )}
        </Box>
        {list && list.length > 0 ? (
          <Box
            sx={{ maxHeight: "60vh", overflowY: "auto", paddingRight: "8px" }}
          >
            <Grid2 container spacing={2}>
              {list?.map((address) => (
                <Grid2
                  size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}
                  key={address.id}
                >
                  <AddressCard
                    address={address}
                    isSelected={
                      Number(selectedAddressInPopup?.id) === Number(address.id)
                    }
                    onSelect={(add) => setSelectedAddressInPopup(add)}
                    onDelete={handleDeleteAddress}
                  />
                </Grid2>
              ))}
            </Grid2>
          </Box>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 5, mb: 5 }}>
            <CustomTypography variant="body1">
              No Addresses Found
            </CustomTypography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 4, pb: 3, mr: 1 }}>
        <AppButton variant="primary" label="Done" onClick={handleDone} />
      </DialogActions>
    </Dialog>
  );
};

export default observer(SelectAddressPopup);
